/**
 * interactions.ts
 * -------------------------------------------------------
 * 职责：订阅 echarts / ZRender 上的鼠标事件，
 *       实现「拖拽不触发点击」、园区散点、政策散点与区块选省/市。
 *       政策模式：全国图单击省区下钻并写入省份筛选；单击省份聚合点仅在右侧展示代表政策，不改左侧筛选、不下钻；省图单击地市更新筛选。
 * -------------------------------------------------------
 */

import type { ECharts } from 'echarts';
import { DRAG_THRESHOLD_PX } from '../mapConstants';
import { provinceAdcodeToGeoName, resolveProvinceAdcodeLoose } from '../provinceAdcode';
import {
    pixelFromGeo3DClickParams,
    resolveLngLatFromGeo3DPixel,
    resolveProvinceAdcodeFromChinaMapPixel,
} from './geoPickHelper';
import {
    featureLngLatByName,
    pickNearestScatterByLngLat,
    pickUniqueScatterByCityName,
    resolveProvinceAdcodeFromClickParams,
} from './geoHelpers';
import type {
    Geo3DClickParams,
    MapInteractionDeps,
    ScatterDatum,
    ZrPointerEvent,
} from '../types';

function pickNearbyProjectScatterFromPixel(
    chart: ECharts,
    x: number,
    y: number,
    deps: MapInteractionDeps,
): ScatterDatum | null {
    const mode = deps.mapQueryModeRef.current;
    if (mode !== 'projects' && mode !== 'parks') return null;
    const items = deps.scatterDataRef.current;
    if (!items.length) return null;
    const lngLat = resolveLngLatFromGeo3DPixel(chart, x, y);
    if (!lngLat) return null;
    const maxDeg = deps.viewCtxRef.current.mapName === 'china' ? 0.55 : 0.45;
    return pickNearestScatterByLngLat<ScatterDatum>(
        items,
        lngLat[0],
        lngLat[1],
        maxDeg,
    );
}

function pickProjectScatterFromGeoClick(
    chart: ECharts,
    params: Geo3DClickParams,
    deps: MapInteractionDeps,
    fallbackPixel: { x: number; y: number } | null,
): ScatterDatum | null {
    const mode = deps.mapQueryModeRef.current;
    if (mode !== 'projects' && mode !== 'parks') return null;
    const items = deps.scatterDataRef.current;
    if (!items.length) return null;

    const pix = pixelFromGeo3DClickParams(params) ?? fallbackPixel;
    if (pix) {
        const nearby = pickNearbyProjectScatterFromPixel(chart, pix.x, pix.y, deps);
        if (nearby) return nearby;
    }

    if (deps.viewCtxRef.current.mapName === 'china') return null;
    const regionName = typeof params.name === 'string' ? params.name.trim() : '';
    if (!regionName) return null;
    return pickUniqueScatterByCityName(items, regionName);
}

function selectScatter(deps: MapInteractionDeps, item: ScatterDatum): void {
    deps.setDrillTargetCoord([item.value[0], item.value[1]]);
    deps.onSelectRef.current(item.parkId);
}

/**
 * 订阅地图交互事件，返回清理函数。
 */
export function subscribeMapInteractions(
    chart: ECharts,
    deps: MapInteractionDeps,
): () => void {
    const zr = chart.getZr();

    let pointerDown = false;
    let movedSinceDown = false;
    let suppressClick = false;
    let consumeNextChartClick = false;
    let downX = 0;
    let downY = 0;

    const onMouseDown = (evt: unknown) => {
        const ev = (evt as ZrPointerEvent).event;
        if (ev && typeof ev.button === 'number' && ev.button !== 0) return;
        pointerDown = true;
        movedSinceDown = false;
        downX = ev?.zrX ?? 0;
        downY = ev?.zrY ?? 0;
    };

    const onMouseMove = (evt: ZrPointerEvent) => {
        if (!pointerDown) return;
        const x = evt.event?.zrX ?? downX;
        const y = evt.event?.zrY ?? downY;
        if (
            !movedSinceDown &&
            (Math.abs(x - downX) > DRAG_THRESHOLD_PX ||
                Math.abs(y - downY) > DRAG_THRESHOLD_PX)
        ) {
            movedSinceDown = true;
        }
    };

    const onMouseUp = () => {
        if (pointerDown && movedSinceDown) {
            suppressClick = true;
        }
        pointerDown = false;
        movedSinceDown = false;
    };

    const onGlobalOut = () => {
        pointerDown = false;
        movedSinceDown = false;
    };

    const onZrClick = (evt: ZrPointerEvent) => {
        if (suppressClick) return;
        const mode = deps.mapQueryModeRef.current;
        if (mode !== 'projects' && mode !== 'parks') return;
        const x = evt.event?.zrX ?? downX;
        const y = evt.event?.zrY ?? downY;
        const nearby = pickNearbyProjectScatterFromPixel(chart, x, y, deps);
        if (!nearby) return;
        consumeNextChartClick = true;
        selectScatter(deps, nearby);
    };

    const onClick = (params: Geo3DClickParams) => {
        if (suppressClick) {
            suppressClick = false;
            consumeNextChartClick = false;
            return;
        }
        if (consumeNextChartClick) {
            consumeNextChartClick = false;
            return;
        }

        const raw = params.data;

        if (raw && typeof raw === 'object' && 'representativePolicyId' in raw) {
            const datum = raw as {
                representativePolicyId: string;
                value: [number, number, number];
                name?: string;
            };
            const id = String(datum.representativePolicyId);
            if (id) {
                deps.setDrillTargetCoord([datum.value[0], datum.value[1]]);
                deps.onSelectRef.current(id);
            }
            return;
        }

        // scatter3D 点击：优先从 data.parkId 读取，若 echarts-gl 未透传自定义字段则
        // 回退到 dataIndex 在 scatterDataRef 中查找
        if (raw && typeof raw === 'object' && 'parkId' in raw) {
            const datum = raw as ScatterDatum;
            const id = String(datum.parkId);
            if (id) {
                deps.setDrillTargetCoord([datum.value[0], datum.value[1]]);
                deps.onSelectRef.current(id);
            }
            return;
        }

        if (
            params.seriesType === 'scatter3D' &&
            typeof params.dataIndex === 'number' &&
            params.componentType === 'series'
        ) {
            // 政策聚合点在某些渲染场景下不会透传自定义字段，回退到 dataIndex 回查
            if (deps.mapQueryModeRef.current === 'policies') {
                const policyItems = deps.policyScatterDataRef.current;
                const policyDatum = policyItems[params.dataIndex];
                if (policyDatum?.representativePolicyId) {
                    deps.setDrillTargetCoord([policyDatum.value[0], policyDatum.value[1]]);
                    deps.onSelectRef.current(String(policyDatum.representativePolicyId));
                    return;
                }
            }

            const items = deps.scatterDataRef.current;
            const datum = items[params.dataIndex];
            if (datum?.parkId) {
                deps.setDrillTargetCoord([datum.value[0], datum.value[1]]);
                deps.onSelectRef.current(datum.parkId);
            }
            return;
        }

        const nearbyScatter = pickProjectScatterFromGeoClick(
            chart,
            params,
            deps,
            { x: downX, y: downY },
        );
        if (nearbyScatter) {
            selectScatter(deps, nearbyScatter);
            return;
        }

        const mapName = deps.viewCtxRef.current.mapName;

        // —— 省下钻图：政策模式下点击地级区块 ——
        if (mapName !== 'china') {
            if (deps.mapQueryModeRef.current !== 'policies') return;
            const cityName =
                typeof params.name === 'string' ? params.name.trim() : '';
            if (!cityName) return;
            deps.onPolicySelectCityNameRef.current?.(cityName);
            return;
        }

        // —— 全国图 ——
        let ad = resolveProvinceAdcodeFromClickParams(params);
        if (!ad) {
            const pix = pixelFromGeo3DClickParams(params);
            if (pix) {
                ad = resolveProvinceAdcodeFromChinaMapPixel(chart, pix.x, pix.y);
            }
        }
        if (!ad) return;

        if (deps.mapQueryModeRef.current === 'policies') {
            const currentProvince = deps.policyProvinceRef.current?.trim();
            const currentProvinceAd = currentProvince
                ? resolveProvinceAdcodeLoose(currentProvince)
                : null;

            // 省级筛选且已选定省份时，优先按省份聚合点语义选中代表政策，避免落入区块下钻导致“点不动”。
            if (
                deps.policyLevelRef.current === 'provincial' &&
                currentProvinceAd &&
                currentProvinceAd === ad
            ) {
                const sameProvinceAgg = deps.policyScatterDataRef.current.find((item) => {
                    const provinceName = item.name.split('·')[0].trim();
                    return resolveProvinceAdcodeLoose(provinceName) === ad;
                });
                if (sameProvinceAgg?.representativePolicyId) {
                    deps.setDrillTargetCoord([sameProvinceAgg.value[0], sameProvinceAgg.value[1]]);
                    deps.onSelectRef.current(String(sameProvinceAgg.representativePolicyId));
                }
                return;
            }

            const geoName = provinceAdcodeToGeoName(ad);
            if (!geoName) return;
            deps.policyDrillByUserRef.current = true;
            deps.setDrillTargetCoord(null);
            deps.onDrillProvinceRef.current?.();
            deps.setFocusAdcode(ad);
            deps.setLayoutMode('provinceDrill');
            deps.onPolicyDrillProvinceNameRef.current?.(geoName);
            return;
        }

        if (deps.mapQueryModeRef.current === 'projects') {
            deps.syncProjectMapFollowTrueRef?.current();
        }
        deps.setDrillTargetCoord(null);
        deps.onDrillProvinceRef.current?.();
        deps.setFocusAdcode(ad);
        deps.setLayoutMode('provinceDrill');
    };

    const onDblClick = (params: Geo3DClickParams) => {
        const raw = params.data;
        if (raw && typeof raw === 'object' && ('parkId' in raw || 'representativePolicyId' in raw)) {
            return;
        }

        if (deps.mapQueryModeRef.current !== 'policies') return;

        const mapName = deps.viewCtxRef.current.mapName;

        if (mapName === 'china') {
            return;
        }

        const cityName =
            typeof params.name === 'string' ? params.name.trim() : '';
        const geo = deps.provinceGeoForPickRef.current;
        if (cityName && geo) {
            const ll = featureLngLatByName(geo, cityName);
            if (ll) deps.setDrillTargetCoord(ll);
        }
    };

    zr.on('mousedown', onMouseDown);
    zr.on('mousemove', onMouseMove);
    zr.on('mouseup', onMouseUp);
    zr.on('globalout', onGlobalOut);
    zr.on('click', onZrClick);
    chart.on('click', onClick);
    chart.on('dblclick', onDblClick);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
        window.removeEventListener('mouseup', onMouseUp);
        zr.off('mousedown', onMouseDown);
        zr.off('mousemove', onMouseMove);
        zr.off('mouseup', onMouseUp);
        zr.off('globalout', onGlobalOut);
        zr.off('click', onZrClick);
        chart.off('click', onClick);
        chart.off('dblclick', onDblClick);
    };
}
